import React, {Fragment, useEffect, useRef, useState} from "react"
import { Label, Button, Spinner } from "reactstrap"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { UpgradePlanIcon } from '@csrc/assets/icons'
import { SelectStyleAction } from "@src/components/helpers/SelectConfigs"
import UpgradeTooltip from "@src/components/FeaturesComponents/modules/common/UpgradeTooltip"
import { trans, uuidv4, toBoolean, checkFeatureAvailability } from "@utils"

import AsyncSelectWithActions from "../AsyncSelectWithActions"
import ReactSelectDropdownButton from "../../ReactSelectDropdownButton"
import {
  _loadSimpleOptions,
  _loadAdvancedDropdownButtonRelationsOptions,
  _loadAdvancedSelectedOptionsWithRelationFilterKeys,
  _fillAdvancedFilterValue,
  _fillDropdownButtonOptionsWithRelations,
  _applyRelationFilter,
  _checkRelationDataOnSubmit,
  _prepareSimpleAdvancedValue,
  _prepareOnChangeAdvancedFilterValue,
  _prepareOnChangeSimpleAdvancedValue
} from "./index.func"
import SlimModalPopup from "../../SlimModalPopup"
import SlimModalPopupForm from "./SlimModalPopupForm"
import Tags from "./index.Tags"

import "@src/assets/scss/advanced-filters.scss"

const Simple = (props) => {
  const { name, value, className = "react-select", classNamePrefix = "select", placeholder = trans("gen.reactSelect.placeholder"), isMulti = true, remoteUrl, remoteUrlFormatter, optionFormatter, extraRemoteParams, styles, onChange } = props
  const [{ value: _value, loaded }, _setValue] = useState({ value: [], loaded: false })

  useEffect(() => {
    if (value && _.size(value) > 0) {
      const isAdvanced = toBoolean(value.query)
      if (!isAdvanced && !loaded) {
        _prepareSimpleAdvancedValue({ value, remoteUrl, remoteUrlFormatter }).then((options) => {
          if (_.size(options) > 0) {
            _setValue({ value: options, loaded: true })
          }
        })
      }
    }
    if (value && _.size(value) === 0) {
      _setValue({ value: [], loaded: true })
    }
  }, [value])

  useEffect(() => {
    const value = _prepareOnChangeSimpleAdvancedValue({ options: _value })
    onChange(value)
  }, [_value])

  return (
    <AsyncSelectWithActions
      name={name}
      value={_value}
      loadOptions={(q, extraRemoteParams) => _loadSimpleOptions({ q, remoteUrl, remoteUrlFormatter, extraRemoteParams })}
      isClearable={true}
      isMulti={isMulti}
      defaultOptions
      cacheOptions
      classNamePrefix={classNamePrefix}
      // theme={selectThemeColors}
      className={className}
      placeholder={placeholder}
      optionFormatter={optionFormatter}
      extraRemoteParams={extraRemoteParams || false}
      noOptionsMessage={() => trans("gen.reactSelect.noOptions")}
      styles={{
        ...SelectStyleAction(props),
        ...styles
      }}
      onChange={(options) => {
        const _options = isMulti ? options : options ? [options] : []
        _setValue({ value: _options, loaded: true })
      }}
    />
  )
}

const Advanced = (props) => {
  const { name, label, advanced = {}, onChange = () => {} } = props
  const [_advanced, _setAdvanced] = useState({
    value: {},
    originalOptions: [],
    options: [],
    relationFilter: {},
    loading: false
  })
  const { options, originalOptions, relationFilter, loading } = _advanced
  const optionsKeyByValue = _.keyBy(options, "value")
  const optionsFiltered = _.filter(options, (o) => !_advanced.value[o.value])
  const slimModalPopupRef = useRef(null)
  const targetUuid = uuidv4()

  const _slimModalPopupFormSubmit = (option, data) => {
    const _relationFilter = _checkRelationDataOnSubmit({ relationFilter, option, data })
    _setAdvanced((_advanced) => ({
      ..._advanced, value: { ..._advanced.value, [option.value]: data }, relationFilter: _relationFilter
    }))
    slimModalPopupRef.current.close()
  }

  const onTagClear = (key, option) => {
    let _relationFilter = {..._advanced.relationFilter}
    if (_.includes(['select', 'asyncSelect'], option.type) && option.has_relations) {
      _relationFilter = _.omit(_relationFilter, option.value)
    }
    const value = _.omit(_advanced.value, key)
    _setAdvanced((_advanced) => ({ ..._advanced, value, relationFilter: _relationFilter }))
  }

  const onClearAll = () => {
    _setAdvanced((_advanced) => ({ ..._advanced, value: {}, relationFilter: {} }))
  }

  useEffect(() => {
    if (_.size(advanced.filters) > 0) {
      _setAdvanced((_advanced) => ({ ..._advanced, loading: true }))
      Promise.all([
        _loadAdvancedSelectedOptionsWithRelationFilterKeys(props.value, advanced.filters),
        _loadAdvancedDropdownButtonRelationsOptions(advanced.filters)
      ]).then((res) => {
        const [selectedOptionWithRelationFilterKeys, relationsArgs] = res
        const { optionsData, relationFilterKeys } = selectedOptionWithRelationFilterKeys
        const options = _fillDropdownButtonOptionsWithRelations(advanced.filters, relationsArgs)
        const _value = _fillAdvancedFilterValue(props.value, optionsData)
        _setAdvanced((_advanced) => ({
          ..._advanced, value: _value, originalOptions: options, options, relationFilter: relationFilterKeys, loading: false
        }))
      })
    }
  }, [])

  useEffect(() => {
    if (_.size(relationFilter) > 0) {
      const { value: filteredValue, options: filteredOptions } = _applyRelationFilter({
        keys: _.flatMap(relationFilter, (v) => v),
        options: originalOptions,
        value: _advanced.value
      })
      _setAdvanced((_advanced) => ({ ..._advanced, value: filteredValue, options: filteredOptions }))
    }
    if (_.size(relationFilter) === 0) {
      _setAdvanced((_advanced) => ({ ..._advanced, options: originalOptions }))
    }
  }, [relationFilter])

  useEffect(() => {
    if (_.size(_advanced.originalOptions) > 0) {
      const value = _prepareOnChangeAdvancedFilterValue(advanced.entity, _advanced)
      onChange(value)
    }
  }, [_advanced.value])

  return (
    <div id={`${name}_${targetUuid}_datatable_filter`} className="position-relative p-50 border border-secondary border-lighten-5 rounded">
      <Tags
        value={_advanced.value}
        options={optionsKeyByValue}
        isLoading={loading}
        onEdit={(data) => slimModalPopupRef.current.open(data)}
        onClear={onTagClear}
      />
      <div className="d-flex justify-content-between" style={{ gap: '0.5rem' }}>
        <ReactSelectDropdownButton
          name={"advanced-filters-dropdown-button"}
          placeholder={trans("gen.advancedFilters.addFilter")}
          options={optionsFiltered}
          isLoading={loading}
          onChange={(option) => slimModalPopupRef.current.open({ option })}
        />
        {!loading && _.size(_advanced.value) > 0 && (
          <Button
            type='button'
            color={'flat-danger'}
            size={'sm'}
            className={'px-50 py-25'}
            onClick={onClearAll}
          >
            {trans('gen.actions.clearAll')}
          </Button>
        )}
      </div>
      <SlimModalPopup ref={slimModalPopupRef} portal={_.get(advanced, 'modalPortal', null)} maxWidth={_.get(advanced, 'modalMaxWidth', '100%')}>
        {(args) => (
          <SlimModalPopupForm
            data={args}
            prefix={label}
            onSubmit={(data) => _slimModalPopupFormSubmit(args.option, data)}
            onClose={() => slimModalPopupRef.current.close()}
          />
        )}
      </SlimModalPopup>
      {loading && <Spinner className="position-absolute" style={{ right: 10, top: 10 }} size="sm" type="grow" />}
    </div>
  )
}

const AdvancedFilters = (props) => {
  const {name, value, onSwitch, onChange} = props
  const query = _.get(value, 'query', false)
  const [isAdvanced, setIsAdvanced] = useState(toBoolean(query))
  const featureAvailableUuid = `${name}_tooltip_${uuidv4().replaceAll('-', '')}`
  const featureAvailable = checkFeatureAvailability('app_advanced_filters')
  const switchAdvancedFiltersComponent = (advanced = false) => {
    setIsAdvanced(advanced)
  }
  useEffect(() => {
    const query = _.get(value, 'query', false)
    if (_.size(value) > 0 && toBoolean(query) !== toBoolean(isAdvanced)) {
      setIsAdvanced(toBoolean(query))
    }
    if (_.size(value) === 0) {
      setIsAdvanced(false)
    }
  }, [value])
  useEffect(() => {
    if (onSwitch) onSwitch(isAdvanced)
  }, [isAdvanced])
  return (
    <div className="advanced-filters-wrapper">
      <div className="d-flex justify-content-between">
        <Label>{props.label}</Label>
        <div className="d-flex align-items-center">
          {isAdvanced && (
            <Button color={"link"} size={"sm"} className={"p-25"} onClick={() => featureAvailable && switchAdvancedFiltersComponent(false)}>
              {trans("gen.advancedFilters.simple")}
            </Button>
          )}
          {!isAdvanced && (
            <Button id={featureAvailableUuid} color={"link"} size={"sm"} className={"p-25"} onClick={() => featureAvailable && switchAdvancedFiltersComponent(true)}>
              <Fragment>
                {trans("gen.advancedFilters.advanced")}
                {!featureAvailable && (
                  <span className={'ml-50'}>
                    <FontAwesomeIcon icon={UpgradePlanIcon}/>
                  </span>
                )}
              </Fragment>
            </Button>
          )}
          {!featureAvailable && <UpgradeTooltip id={featureAvailableUuid} featureKey={'app_advanced_filters'} />}
        </div>
      </div>
      {isAdvanced && <Advanced {...props} />}
      {!isAdvanced && <Simple {...props} />}
    </div>
  )
}

export default AdvancedFilters
